import React from 'react';
import JsonView from '@uiw/react-json-view';
import { lightTheme } from '@uiw/react-json-view/light';
import { darkTheme } from '@uiw/react-json-view/dark';
// import { useColorScheme } from 'react-native'; // Incorrect for web
import { PolicyTemplate } from '../../../types/policy'; // Corrected path
// import { usePolicyVisualizer } from '../../../context/PolicyVisualizerContext'; // Corrected path

// Define props passed down from PolicyVisualizer
interface TreeViewProps {
  policy: PolicyTemplate;
  // Add other props like onUpdate callbacks as needed
}

// Utility: Recursively sanitize alias strings (remove parenthesised property path)
const sanitizeAliases = (input: unknown): unknown => {
  if (Array.isArray(input)) {
    return input.map(sanitizeAliases);
  }
  if (input && typeof input === 'object' && input !== null) {
    const output: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      if (key === 'field' && typeof value === 'string') {
        output[key] = value.split(' (')[0].trim();
      } else if (key === 'count' && value && typeof value === 'object' && value !== null && 'field' in value) {
        // Sanitize count.field while preserving other properties
        const countValue = value as Record<string, unknown>;
        output[key] = {
          ...countValue,
          field: typeof countValue.field === 'string' ? countValue.field.split(' (')[0].trim() : countValue.field,
        };
        // Also recursively sanitize nested properties like where
        for (const [innerKey, innerVal] of Object.entries(output[key] as Record<string, unknown>)) {
          if (innerKey !== 'field') {
            (output[key] as Record<string, unknown>)[innerKey] = sanitizeAliases(innerVal);
          }
        }
      } else {
        output[key] = sanitizeAliases(value);
      }
    }
    return output;
  }
  return input;
};

export const TreeView: React.FC<TreeViewProps> = ({ policy /*, other props */ }) => {
  // Custom theme with colors that match your application theme
  const customTheme = {
    ...darkTheme,
    // Override text colors to match your theme
    '--w-rjv-color': '#ffffff', // Main text color (white to match your text-white class)
    '--w-rjv-key-string': '#9cdcfe', // Object key colors (light blue)
    '--w-rjv-key-number': '#9cdcfe', // Array index colors
    
    // Value type colors
    '--w-rjv-type-string-color': '#ce9178', // String values (light orange)
    '--w-rjv-type-int-color': '#b5cea8', // Number values (light green)
    '--w-rjv-type-float-color': '#b5cea8', // Float values (light green)
    '--w-rjv-type-boolean-color': '#569cd6', // Boolean values (blue)
    '--w-rjv-type-null-color': '#569cd6', // Null values (blue)
    '--w-rjv-type-undefined-color': '#569cd6', // Undefined values (blue)
    
    // Punctuation colors
    '--w-rjv-curlybraces-color': '#d4d4d4', // { } braces
    '--w-rjv-brackets-color': '#d4d4d4', // [ ] brackets
    '--w-rjv-colon-color': '#d4d4d4', // : colons
    '--w-rjv-quotes-color': '#9cdcfe', // Key quotes
    '--w-rjv-quotes-string-color': '#ce9178', // String value quotes
    
    // Other elements
    '--w-rjv-arrow-color': '#838383', // Expand/collapse arrows
    '--w-rjv-info-color': '#9c9c9c7a', // Object/array size info
    '--w-rjv-copied-color': '#9cdcfe', // Copy icon
    '--w-rjv-copied-success-color': '#28a745', // Copy success feedback
    
    // Background (keep transparent to match your container)
    '--w-rjv-background-color': 'transparent',
    '--w-rjv-font-family': 'monospace'
  };

  // Sanitize policy structure
  const sanitizedPolicy = React.useMemo(() => sanitizeAliases(policy) as object, [policy]);

  return (
    <div className="h-full overflow-auto border border-gray-600 rounded-lg bg-transparent text-white font-mono p-2">
      <JsonView
        value={sanitizedPolicy}
        displayDataTypes={false}
        displayObjectSize={false}
        enableClipboard={true}
        style={customTheme}
      />
    </div>
  );
}; 